import React, { useCallback, useRef, useState } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, Input, useSlug } from '@bloomberg/media-ui-core';
import { Exit, Search as SearchIcon } from '@bloomberg/media-ui-icons';
import MediaServicesSearchClient from '@foundation/media-services-search-client';

import { DropdownMenuMobile } from '../DropdownMenuMobile';
import { useFetchThrottledSearchResults } from '../hooks/useFetchThrottledSearchResults';
import { THEMES } from '../types';
import { useTranslateMessage } from '../utils/translate-messages';

import styles from './SearchMobile.module.css';

export type SearchMobileProps = GlobalProps & {
  isSearchActive?: boolean;
  setIsSearchActive?: (isActive: boolean) => void;
  searchClient: MediaServicesSearchClient;
  theme?: THEMES;
  trackingFunction: (action: string, eventLabel: string) => void;
  countryCode?: string;
};

export const SearchMobile = React.forwardRef<
  HTMLInputElement,
  SearchMobileProps
>(function SearchMobileComponent(props: SearchMobileProps, ref) {
  const {
    isSearchActive = false,
    setIsSearchActive = () => {},
    name = 'search-mobile',
    unstyled,
    searchClient,
    theme = THEMES.DARK,
    trackingFunction,
    countryCode = 'US',
  } = props;

  const [searchValue, setSearchValue] = useState('');
  const [searchResults, fetchThrottledSearchResults] =
    useFetchThrottledSearchResults();
  const componentName = useSlug(name);
  const translateMessage = useTranslateMessage();

  const _trackingFunction = (
    submenuText: string,
    dropdownListItemText: string
  ) => {
    trackingFunction('click', `search-${submenuText}-${dropdownListItemText}`);
  };

  const clearInputButtonRef = useRef<HTMLButtonElement>(null);

  const cx = classes(
    [
      styles.searchMobileContainer,
      isSearchActive && styles.isSearchActive,
      {
        [styles.dark]: theme === THEMES.DARK,
      },
    ],
    {
      unstyled,
      name,
    }
  );

  const clearInput = useCallback(() => {
    setSearchValue('');
    setIsSearchActive(false);
  }, []);

  const onInputChange = useCallback(
    (inputValue: string) => {
      setSearchValue(inputValue);
      fetchThrottledSearchResults(inputValue, searchClient, countryCode);
    },
    [searchClient, countryCode]
  );

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.currentTarget.value && event.key === 'Enter') {
      trackingFunction('click', `search-InputQuerySearch-${searchValue}`);
      window.location.href = `/search?query=${event.currentTarget.value}`;
    }
  };

  const inputProps = {
    onFocus: () => setIsSearchActive(true),
  };

  return (
    <div className={cx} ref={ref} data-component={componentName} tabIndex={0}>
      <div className={styles.inputContainer}>
        <Input
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className={styles.input}
          id="bloomberg.com-search-mobile"
          inputProps={inputProps}
          placeholder={translateMessage({
            id: 'media-ui-nav_searchBloomberg',
            defaultMessage: 'Search Bloomberg',
          })}
          aria-label={translateMessage({
            id: 'media-ui-nav_arialabel_searchBloomberg',
            defaultMessage: 'Search Bloomberg',
          })}
          value={searchValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onEnterKeyDown}
        />
        <SearchIcon className={styles.searchIcon} />
      </div>
      {isSearchActive && (
        <DropdownMenuMobile
          key="search-dropdown-menu-mobile"
          menuItemId="search-dropdown-menu-mobile"
          theme={theme}
          submenuItems={searchResults}
          trackingFunction={_trackingFunction}
        />
      )}
      <button
        ref={clearInputButtonRef}
        className={styles.clearInputButton}
        title="cancel"
        type="button"
        aria-label={translateMessage({
          id: 'media-ui-nav_arialabel_cancelButton',
          defaultMessage: 'Cancel',
        })}
        onClick={clearInput}
      >
        <Exit className={styles.exitIcon} />
      </button>
    </div>
  );
});
